import { useEffect, useState } from 'react';

/**
 * Delay changes to a value for a certain amount of time. If the value changes again before the required time has passed, the timer will reset. This means that if a value is repeatedly changed fast enough, it'll never update the debounced value.
 *
 * Useful for values which result in API calls when changed, but you don't want to make repeated API calls (such as the user typing a search query term)
 * @param value The value to debounce
 * @param delayMs The amount of time to debounce by (in milliseconds)
 * @returns The debounced value
 * @example
 * ```ts
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 500);
 *
 * return (
 *   <>
 *     <input onChange={(e) => setSearchTerm(e.target.value)} />
 *     <ResultsList query={debouncedSearchTerm} />
 *   </>
 * )
 * ```
 * @see https://usehooks.com/usedebounce
 */
export function useDebounce<T>(value: T, delayMs: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => clearTimeout(handler);
  }, [value, delayMs]);

  return debouncedValue;
}
